import { create } from 'zustand'
import { PANEL_DEFAULTS } from '../config/dashboardPanelDefaults'

// --- Per-type indicator settings ---

export interface BatterySettings {
  orientation: 'vertical' | 'horizontal'
  iconWidth: number
  iconHeightRatio: number
  capWidth: number
  capHeight: number
  fillPadding: number
  cornerRadius: number
  iconTextGap: number
  valueFontSize: number
  unitFontSize: number
  labelFontSize: number
}

export interface ThermometerSettings {
  orientation: 'vertical' | 'horizontal'
  tubeWidth: number
  tubeHeightRatio: number
  bulbRadius: number
  fillPadding: number
  iconTextGap: number
  valueFontSize: number
  unitFontSize: number
  labelFontSize: number
}

export interface GaugeSettings {
  arcAngle: number
  trackWidth: number
  needleLength: number
  needleWidth: number
  iconTextGap: number
  valueFontSize: number
  unitFontSize: number
  labelFontSize: number
}

const DEFAULT_BATTERY: BatterySettings = {
  orientation: 'vertical',
  iconWidth: 52,
  iconHeightRatio: 0.48,
  capWidth: 20,
  capHeight: 8,
  fillPadding: 4,
  cornerRadius: 6,
  iconTextGap: 16,
  valueFontSize: 40,
  unitFontSize: 18,
  labelFontSize: 12,
}

const DEFAULT_THERMOMETER: ThermometerSettings = {
  orientation: 'vertical',
  tubeWidth: 16,
  tubeHeightRatio: 0.42,
  bulbRadius: 18,
  fillPadding: 3,
  iconTextGap: 16,
  valueFontSize: 40,
  unitFontSize: 18,
  labelFontSize: 12,
}

const DEFAULT_GAUGE: GaugeSettings = {
  arcAngle: 270,
  trackWidth: 8,
  needleLength: 0.85,
  needleWidth: 4,
  iconTextGap: 16,
  valueFontSize: 36,
  unitFontSize: 18,
  labelFontSize: 12,
}

// --- Panel override (per-POI) ---

export interface PanelOverride {
  offset: [number, number, number]
  rotationY: number
  scale: number
  bgOpacity: number
  panelOpacity: number
  glowIntensity: number
  scanLineSpeed: number
  scanLineOpacity: number
  accentColor: string
  textColor: string
  bgColor: string
  visible: boolean
  heading: string
  padding: number
  panelHeight: number
  panelWidth: number
  battery: BatterySettings
  thermometer: ThermometerSettings
  gauge: GaugeSettings
}

// --- Global styles ---

export interface GlobalPanelStyles {
  accentColor: string
  textColor: string
  bgColor: string
  bgOpacity: number
  glowIntensity: number
  scanLineSpeed: number
  scanLineOpacity: number
  borderOpacity: number
  padding: number
  panelHeight: number
  panelWidth: number
  battery: BatterySettings
  thermometer: ThermometerSettings
  gauge: GaugeSettings
}

const DEFAULT_GLOBAL_STYLES: GlobalPanelStyles = {
  accentColor: '#4fc3f7',
  textColor: '#ffffff',
  bgColor: '#0a0a1a',
  bgOpacity: 0.15,
  glowIntensity: 0.3,
  scanLineSpeed: 0.5,
  scanLineOpacity: 0.15,
  borderOpacity: 0.2,
  padding: 24,
  panelHeight: 3,
  panelWidth: 3,
  battery: { ...DEFAULT_BATTERY },
  thermometer: { ...DEFAULT_THERMOMETER },
  gauge: { ...DEFAULT_GAUGE },
}

interface DashboardPanelState {
  overrides: Record<string, Partial<PanelOverride>>
  globalStyles: GlobalPanelStyles
  isDirty: boolean
  isSaving: boolean
  isServerLoaded: boolean

  setOverride: (poiId: string, partial: Partial<PanelOverride>) => void
  setGlobalStyle: <K extends keyof GlobalPanelStyles>(key: K, value: GlobalPanelStyles[K]) => void
  setIndicatorSetting: <T extends 'battery' | 'thermometer' | 'gauge'>(
    type: T,
    key: string,
    value: GlobalPanelStyles[T][keyof GlobalPanelStyles[T]]
  ) => void
  getEffectiveConfig: (poiId: string) => Required<PanelOverride>

  saveToServer: () => Promise<void>
  loadFromServer: () => Promise<void>
  resetToDefaults: () => void
}

export const useDashboardPanelStore = create<DashboardPanelState>((set, get) => ({
  overrides: {},
  globalStyles: { ...DEFAULT_GLOBAL_STYLES },
  isDirty: false,
  isSaving: false,
  isServerLoaded: false,

  setOverride: (poiId, partial) =>
    set((s) => ({
      overrides: {
        ...s.overrides,
        [poiId]: { ...s.overrides[poiId], ...partial },
      },
      isDirty: true,
    })),

  setGlobalStyle: (key, value) =>
    set((s) => ({
      globalStyles: { ...s.globalStyles, [key]: value },
      isDirty: true,
    })),

  setIndicatorSetting: (type, key, value) =>
    set((s) => ({
      globalStyles: {
        ...s.globalStyles,
        [type]: { ...s.globalStyles[type], [key]: value },
      },
      isDirty: true,
    })),

  getEffectiveConfig: (poiId) => {
    const { globalStyles, overrides } = get()
    const panelDefault = PANEL_DEFAULTS[poiId]
    const override = overrides[poiId]

    return {
      // Per-panel properties (from override or panel defaults)
      offset: override?.offset ?? panelDefault?.offset ?? [0, 2, 0],
      rotationY: override?.rotationY ?? panelDefault?.rotationY ?? 0,
      scale: override?.scale ?? panelDefault?.scale ?? 1.0,
      panelOpacity: override?.panelOpacity ?? 1.0,
      visible: override?.visible ?? true,
      heading: override?.heading ?? '',
      panelWidth: override?.panelWidth ?? globalStyles.panelWidth,
      // Global style properties (always from globalStyles)
      accentColor: globalStyles.accentColor,
      textColor: globalStyles.textColor,
      bgColor: globalStyles.bgColor,
      bgOpacity: globalStyles.bgOpacity,
      glowIntensity: globalStyles.glowIntensity,
      scanLineSpeed: globalStyles.scanLineSpeed,
      scanLineOpacity: globalStyles.scanLineOpacity,
      padding: globalStyles.padding,
      panelHeight: globalStyles.panelHeight,
      // Per-type indicator settings (always from globalStyles)
      battery: globalStyles.battery,
      thermometer: globalStyles.thermometer,
      gauge: globalStyles.gauge,
    }
  },

  saveToServer: async () => {
    set({ isSaving: true })
    console.log('[DashboardPanelStore] Save disabled in static mode')
    set({ isDirty: false, isSaving: false })
  },

  loadFromServer: async () => {
    try {
      const res = await fetch('/data/dashboard-panel-config.json')
      if (res.ok) {
        const data = await res.json()
        if (data && typeof data === 'object' && data.overrides) {
          const loaded = data.globalStyles ?? {}
          set({
            overrides: data.overrides,
            globalStyles: {
              ...DEFAULT_GLOBAL_STYLES,
              ...loaded,
              battery: { ...DEFAULT_BATTERY, ...loaded.battery },
              thermometer: { ...DEFAULT_THERMOMETER, ...loaded.thermometer },
              gauge: { ...DEFAULT_GAUGE, ...loaded.gauge },
            },
            isDirty: false,
          })
        }
      }
    } catch {
      console.log('[DashboardPanelStore] Config not available, using defaults')
    } finally {
      set({ isServerLoaded: true })
    }
  },

  resetToDefaults: () =>
    set({ overrides: {}, globalStyles: { ...DEFAULT_GLOBAL_STYLES }, isDirty: true }),
}))
